import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useLayoutEffect, useState } from "react";
import HowItWorksPage from "@/pages/HowItWorksPage";
import OnlyBestPage from "@/pages/OnlyBestPage";
import OnlyBestApplyPage from "@/pages/OnlyBestApplyPage";
import OnlyBestStreamersPage from "@/pages/OnlyBestStreamersPage";
import DirectoryPage from "@/pages/DirectoryPage";
import MarketsPage from "@/pages/MarketsPage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import AIBusinessHeroesPage from "@/pages/AIBusinessHeroesPage";
import LiveContentPage from "@/pages/LiveContentPage";
import SubmitBusinessPage from "@/pages/SubmitBusinessPage";
import BadgeRedirectPage from "@/pages/BadgeRedirectPage";
import BadgeEmbedPage from "@/pages/BadgeEmbedPage";
import GetFundedPage from "@/pages/GetFundedPage";
import PostcardThatTalksBackPage from "@/pages/PostcardThatTalksBackPage";
import TVAdsPlatformPage from "@/pages/TVAdsPlatformPage";
import AdminDashboard from "@/pages/AdminDashboard";
import MarketplacePage from "@/pages/MarketplacePage";
import BestOfBestPage from "@/pages/BestOfBestPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import InventoryDetailPage from "@/pages/InventoryDetailPage";
import ComparePage from "@/pages/ComparePage";
import AffiliateRequestPage from "@/pages/AffiliateRequestPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import AIUserPolicyPage from "@/pages/AIUserPolicyPage";
import AISearchPage from "@/pages/AISearchPage";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CreateListingModal from "@/components/CreateListingModal";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { TrackingProvider } from "@/contexts/TrackingContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DashboardOverview from "@/pages/dashboard/DashboardOverview";
import ListingManagement from "@/pages/dashboard/ListingManagement";
import DashboardSettings from "@/pages/dashboard/DashboardSettings";
import DashboardLeads from "@/pages/dashboard/DashboardLeads";
import DashboardActivity from "@/pages/dashboard/DashboardActivity";
import DashboardComingSoon from "@/pages/dashboard/DashboardComingSoon";
import AdminUsers from "@/pages/dashboard/AdminUsers";
import AdminGHL from "@/pages/dashboard/AdminGHL";
import AdminClaims from "@/pages/dashboard/AdminClaims";
import AdminAllListings from "@/pages/dashboard/AdminAllListings";
import DashboardPixels from "@/pages/dashboard/DashboardPixels";
import DashboardContent from "@/pages/dashboard/DashboardContent";
import DashboardComments from "@/pages/dashboard/DashboardComments";
import DashboardInventory from "@/pages/dashboard/DashboardInventory";
import DashboardSavedSearches from "@/pages/dashboard/DashboardSavedSearches";
import DashboardIntegrations from "@/pages/dashboard/DashboardIntegrations";
import AdminApiKeys from "@/pages/dashboard/AdminApiKeys";
import AdminRecommendations from "@/pages/dashboard/AdminRecommendations";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import SharePromote from "@/pages/dashboard/SharePromote";
import AnalyticsDashboard from "@/pages/dashboard/AnalyticsDashboard";
import DashboardReferrals from "@/pages/dashboard/DashboardReferrals";
import OnlyBestCampaign from "@/pages/dashboard/OnlyBestCampaign";
import OnlyBestStats from "@/pages/dashboard/OnlyBestStats";
import LeaderboardPage from "@/pages/LeaderboardPage";
import CreatorsDirectoryPage from "@/pages/CreatorsDirectoryPage";
import LiveEventPage from "@/pages/LiveEventPage";
import LiveEventsPage from "@/pages/dashboard/LiveEventsPage";
import LivecastsPage from "@/pages/dashboard/LivecastsPage";
import EmbedsPage from "@/pages/dashboard/EmbedsPage";
import LivecastPage from "@/pages/LivecastPage";
import TrackingDebugPanel from "@/components/TrackingDebugPanel";

// Scroll to top on route change - useLayoutEffect ensures scroll before paint
function ScrollToTop() {
  const { pathname } = useLocation();
  
  // Disable browser's automatic scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);
  
  useLayoutEffect(() => {
    // Temporarily disable smooth scroll behavior for instant scroll
    const htmlElement = document.documentElement;
    const originalScrollBehavior = htmlElement.style.scrollBehavior;
    htmlElement.style.scrollBehavior = 'auto';
    
    // Force scroll to top using multiple methods
    window.scrollTo(0, 0);
    htmlElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also use requestAnimationFrame to ensure scroll happens after render
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      htmlElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
    
    // Restore scroll behavior after a brief delay
    const timeoutId = setTimeout(() => {
      htmlElement.style.scrollBehavior = originalScrollBehavior;
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [pathname]);
  
  return null;
}

// Layout wrapper to conditionally show Nav/Footer
function AppLayout({ children, onOpenCreateListing }) {
  const location = useLocation();
  const isAdminPage = location.pathname === "/admin";
  const isDashboardPage = location.pathname.startsWith("/dashboard");
  const isAuthPage = location.pathname.startsWith("/auth");
  const isEmbedPage = location.pathname.startsWith("/embed/");
  const hideChrome = isAdminPage || isDashboardPage || isAuthPage || isEmbedPage;
  
  return (
    <>
      {!hideChrome && <Navigation onOpenCreateListing={onOpenCreateListing} />}
      {children}
      {!hideChrome && <Footer />}
    </>
  );
}

// Listens for ?create=true query param and opens the Create Listing modal
function CreateListingTrigger({ onOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "true") {
      onOpen();
      // Clean up the URL param via react-router so location.search actually updates
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, onOpen, navigate]);
  return null;
}

function App() {
  const [isCreateListingOpen, setIsCreateListingOpen] = useState(false);
  
  return (
    <HelmetProvider>
      <TrackingProvider>
        <AuthProvider>
          <LanguageProvider>
          <div className="min-h-screen bg-[#020817]">
            <BrowserRouter>
              <ScrollToTop />
              <ImpersonationBanner />
              <CreateListingTrigger onOpen={() => setIsCreateListingOpen(true)} />
              <AppLayout onOpenCreateListing={() => setIsCreateListingOpen(true)}>
                <Routes>
                  <Route path="/" element={<DirectoryPage />} />
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route path="/onlybest" element={<OnlyBestPage />} />
                  <Route path="/onlybest/apply" element={<OnlyBestApplyPage />} />
                  <Route path="/onlybest-streamers" element={<OnlyBestStreamersPage />} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route path="/creators" element={<CreatorsDirectoryPage />} />
                  <Route path="/live/:slug/:eventSlug" element={<LiveEventPage />} />
                  <Route path="/ai-search" element={<AISearchPage />} />
                  <Route path="/directory" element={<DirectoryPage />} />
                  <Route path="/directory/:category" element={<DirectoryPage />} />
                  <Route path="/markets" element={<MarketsPage />} />
                  {/* SEO-friendly business URL structure */}
                  <Route path="/directory/:category/:cityState/:slug" element={<BusinessProfilePage />} />
                  <Route path="/best-of-best" element={<BestOfBestPage />} />
                  {/* Legacy URL support */}
                  <Route path="/business/:slug" element={<BusinessProfilePage />} />
                  <Route path="/ai-heroes" element={<AIBusinessHeroesPage />} />
                  <Route path="/live" element={<LiveContentPage />} />
                  <Route path="/marketplace" element={<Navigate to="/marketplace/vehicles" replace />} />
                  <Route path="/marketplace/:type" element={<MarketplacePage />} />
                  {/* Compare page for marketplace items */}
                  <Route path="/compare/:type" element={<ComparePage />} />
                  {/* Marketplace inventory detail pages */}
                  <Route path="/marketplace/:type/:id" element={<InventoryDetailPage />} />
                  <Route path="/listing/:id" element={<ListingDetailPage />} />
                  {/* Inventory pages for POD feed items */}
                  <Route path="/inventory/:type/:id" element={<InventoryDetailPage />} />
                  <Route path="/submit" element={<SubmitBusinessPage />} />
                  <Route path="/badge/:slug" element={<BadgeRedirectPage />} />
                  <Route path="/embed/badge/:slug" element={<BadgeEmbedPage />} />
                  <Route path="/get-funded" element={<GetFundedPage />} />
                  <Route path="/tv-ads-platform" element={<TVAdsPlatformPage />} />
                  <Route path="/postcard-that-talks-back" element={<PostcardThatTalksBackPage />} />
                  <Route path="/livecast/:listingSlug/:castSlug" element={<LivecastPage />} />
                  <Route path="/affiliate-request" element={<AffiliateRequestPage />} />
                  {/* Legal Pages */}
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/ai-policy" element={<AIUserPolicyPage />} />
                  <Route path="/admin" element={<AdminDashboard />} />

                  {/* Auth Pages */}
                  <Route path="/auth/login" element={<LoginPage />} />
                  <Route path="/auth/register" element={<RegisterPage />} />
                  <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                  {/* Friendly aliases */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />

                  {/* Knowledge Base */}
                  <Route path="/knowledge-base" element={<KnowledgeBasePage />} />

                  {/* Dashboard (Protected) */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<DashboardOverview />} />
                    <Route path="listing" element={<ListingManagement />} />
                    <Route path="leads" element={<DashboardLeads />} />
                    <Route path="activity" element={<DashboardActivity />} />
                    <Route path="settings" element={<DashboardSettings />} />
                    <Route path="media" element={<DashboardComingSoon />} />
                    <Route path="content" element={<DashboardContent />} />
                    <Route path="comments" element={<DashboardComments />} />
                    <Route path="inventory" element={<DashboardInventory />} />
                    <Route path="saved-searches" element={<DashboardSavedSearches />} />
                    <Route path="reviews" element={<DashboardComingSoon />} />
                    <Route path="pixels" element={<DashboardPixels />} />
                    <Route path="embeds" element={<DashboardComingSoon />} />
                    <Route path="team" element={<DashboardComingSoon />} />
                    <Route path="billing" element={<DashboardComingSoon />} />
                    <Route path="integrations" element={<DashboardIntegrations />} />
                    <Route path="share" element={<SharePromote />} />
                    <Route path="referrals" element={<DashboardReferrals />} />
                    <Route path="onlybest" element={<OnlyBestCampaign />} />
                    <Route path="onlybest/stats" element={<OnlyBestStats />} />
                    <Route path="live-events" element={<LiveEventsPage />} />
                    <Route path="livecasts" element={<LivecastsPage />} />
                    <Route path="embeds" element={<EmbedsPage />} />
                    <Route path="analytics" element={<AnalyticsDashboard />} />
                    <Route path="admin/users" element={<AdminUsers />} />
                    <Route path="admin/claims" element={<AdminClaims />} />
                    <Route path="admin/listings" element={<AdminAllListings />} />
                    <Route path="admin/api" element={<AdminApiKeys />} />
                    <Route path="admin/ghl" element={<AdminGHL />} />
                    <Route path="admin/recommendations" element={<AdminRecommendations />} />
                  </Route>
                </Routes>
              </AppLayout>
              <CreateListingModal 
                isOpen={isCreateListingOpen} 
                onClose={() => setIsCreateListingOpen(false)} 
              />
              <CookieConsentBanner />
              <TrackingDebugPanel />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </div>
          </LanguageProvider>
        </AuthProvider>
      </TrackingProvider>
    </HelmetProvider>
  );
}

export default App;